import { Button } from "@egaranti/components";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@egaranti/components";
import { TableSkeleton } from "@egaranti/components";
import { useToast } from "@egaranti/components";

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import useCampaignsStore from "@/stores/useCampaignsStore";

import ApplicationDetailModal from "@/components/campaigns/applicationDetailModal";
import ApplicationsFilterSheet from "@/components/campaigns/applicationsFilterSheet";
import ApplicationsFilterTags from "@/components/campaigns/applicationsFilterTags";
import EmptyTable from "@/components/shared/EmptyTable";
import TableHeaderWrapper from "@/components/shared/tableHeaderWrapper";

import FilterIcon from "@/assets/icons/filter.svg?react";

import { MoreHorizontal, FileText, ImageIcon } from "lucide-react";

const Applications = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    applications,
    loading,
    applicationFilters: filters,
    applicationError,
    getApplications,
    getValidCampaigns,
    setApplicationFilters: setFilters,
    approveApplication,
    rejectApplication,
  } = useCampaignsStore();

  const [selectedApplication, setSelectedApplication] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [openFilterSheet, setOpenFilterSheet] = useState(false);

  // Initialize from URL params on mount
  useEffect(() => {
    const urlStatus = searchParams.get("status") || "ALL";
    const urlCampaignId = searchParams.get("campaignId") || "";

    setFilters({
      ...filters,
      status: urlStatus,
      campaignId: urlCampaignId,
    });

    getValidCampaigns();
    getApplications();
  }, []);

  // Sync filters to URL
  useEffect(() => {
    const params = {};
    // tab parametresini koru
    const currentTab = searchParams.get("tab");
    if (currentTab) {
      params.tab = currentTab;
    }
    if (filters.status && filters.status !== "ALL") {
      params.status = filters.status;
    }
    if (filters.campaignId) {
      params.campaignId = filters.campaignId;
    }
    setSearchParams(params);
  }, [filters.status, filters.campaignId]);

  const handleSearch = (e) => {
    const newFilters = { ...filters, query: e.target.value };
    setFilters(newFilters);
    getApplications();
  };

  const openModal = (application) => {
    setSelectedApplication(application);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedApplication(null);
  };

  const handleApprove = async (application) => {
    try {
      await approveApplication(application.id);
      toast({
        title: t("common.success", "Başarılı"),
        description: t("applicationsPage.approveSuccess", "Başvuru onaylandı"),
        variant: "success",
      });
      closeModal();
    } catch (error) {
      toast({
        title: t("common.error", "Hata"),
        description: t(
          "applicationsPage.approveError",
          "Başvuru onaylanırken hata oluştu",
        ),
        variant: "error",
      });
    }
  };

  const handleReject = async (application, reason) => {
    try {
      await rejectApplication(application.id, reason);
      toast({
        title: t("common.success", "Başarılı"),
        description: t("applicationsPage.rejectSuccess", "Başvuru reddedildi"),
        variant: "success",
      });
      closeModal();
    } catch (error) {
      toast({
        title: t("common.error", "Hata"),
        description: t(
          "applicationsPage.rejectError",
          "Başvuru reddedilirken hata oluştu",
        ),
        variant: "error",
      });
      throw error;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "APPROVED":
        return (
          <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
            {t("applicationsPage.status.approved", "Onaylandı")}
          </span>
        );
      case "REJECTED":
        return (
          <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800">
            {t("applicationsPage.status.rejected", "Reddedildi")}
          </span>
        );
      case "PROCESSED":
        return (
          <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800">
            {t("applicationsPage.status.processed", "İşlendi")}
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-medium text-yellow-800">
            {t("applicationsPage.status.pending", "Beklemede")}
          </span>
        );
    }
  };

  return (
    <div className="rounded-lg">
      <div className="flex flex-col rounded-t-lg bg-white pb-4">
        <TableHeaderWrapper className="flex items-center justify-between gap-5">
          <ApplicationsFilterTags />
          <Button
            className="min-w-[150px] font-medium"
            onClick={() => setOpenFilterSheet(true)}
            variant="secondaryGray"
          >
            <FilterIcon />
            <span>{t("common.addFilter", "Filtre Ekle")}</span>
          </Button>
        </TableHeaderWrapper>

        {applicationError ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <p className="text-sm text-red-600">{applicationError}</p>
            <Button
              variant="secondaryGray"
              className="mt-4"
              onClick={() => getApplications()}
            >
              {t("common.tryAgain", "Tekrar Dene")}
            </Button>
          </div>
        ) : loading ? (
          <Table>
            <TableSkeleton colSpan={8} />
          </Table>
        ) : applications.length ? (
          <Table>
            <TableHeader>
              <TableHead>{t("applicationsPage.customer", "Müşteri")}</TableHead>
              <TableHead>{t("applicationsPage.product", "Ürün")}</TableHead>
              <TableHead>{t("applicationsPage.serialNumber", "Seri No")}</TableHead>
              <TableHead>{t("applicationsPage.invoice", "Fatura")}</TableHead>
              <TableHead>
                {t("applicationsPage.campaign", "Kampanya")}
              </TableHead>
              <TableHead>{t("applicationsPage.date", "Tarih")}</TableHead>
              <TableHead>{t("applicationsPage.status", "Durum")}</TableHead>
              <TableHead>{t("common.actions", "İşlemler")}</TableHead>
            </TableHeader>
            <TableBody>
              {applications?.map((app) => (
                <TableRow
                  role="button"
                  onClick={() => openModal(app)}
                  key={app.id}
                >
                  <TableCell>
                    <div>
                      <p className="font-medium text-gray-900">
                        {app.customerFirstName} {app.customerLastName}
                      </p>
                      <p className="text-sm text-gray-500">
                        {app.customerEmail}
                      </p>
                      <p className="text-sm text-gray-500">
                        {app.customerPhone}
                      </p>
                    </div>
                  </TableCell>
                  <TableCell className="font-medium">
                    {app.merchantProductName || "-"}
                  </TableCell>
                  <TableCell className="text-sm text-gray-600">
                    {app.serialNumber || "-"}
                  </TableCell>
                  <TableCell>
                    {app.billingDocumentUrl ? (
                      <span className="inline-flex items-center gap-1 text-green-600" title={t("applicationsPage.invoiceUploaded", "Fatura yüklendi")}>
                        <ImageIcon className="h-4 w-4" />
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-gray-300" title={t("applicationsPage.noInvoice", "Fatura yüklenmemiş")}>
                        <ImageIcon className="h-4 w-4" />
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <span className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700">
                      {app.campaignName}
                    </span>
                  </TableCell>
                  <TableCell>{app.createdAt || "-"}</TableCell>
                  <TableCell>{getStatusBadge(app.applicantStatus)}</TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        openModal(app);
                      }}
                    >
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <EmptyTable
            title={t(
              "applicationsPage.noApplicationsFound",
              "Başvuru bulunamadı",
            )}
            desc={t(
              "applicationsPage.noApplicationsFoundDesc",
              "Henüz kampanya başvurusu bulunmuyor",
            )}
          />
        )}
      </div>

      <ApplicationsFilterSheet
        open={openFilterSheet}
        onOpenChange={setOpenFilterSheet}
      />

      <ApplicationDetailModal
        isOpen={isModalOpen}
        onClose={closeModal}
        application={selectedApplication}
        onApprove={handleApprove}
        onReject={handleReject}
      />
    </div>
  );
};

export default Applications;
